import { useState } from 'react';
import type { LotteryQuoteDto, MatchDetailDto, MatchDto, MovementStepDto } from '../api';
import type { CalculatorMatchSelection } from '@nowscore/core';
import { LotteryCalculatorMarkets } from './LotteryCalculatorMarkets';
import { dataNoteLabel, formatKickoff } from '../lib/format';
import { HAD_SIDE_LABELS, LOTTERY_MARKET_LABELS, impliedRates, lotteryQuote, lotteryQuotes, lotterySelectionLabel, quoteFor, trendArrow, type Bookmaker } from '../lib/odds';
import { MovementChart } from './Bookmakers';

export function LotteryOddsBlock({ match, compact = false }: { match: MatchDto; compact?: boolean }) {
  const had = lotteryQuotes(match, 'had');
  const hhad = lotteryQuotes(match, 'hhad');
  const note = dataNoteLabel(match.lottery?.dataNote);
  if (had.length === 0 && hhad.length === 0) {
    return <div className="lottery-block empty"><span className="lottery-block-title">竞彩赔率</span><span className="lottery-empty">{note ?? '暂无数据'}</span></div>;
  }
  const cell = (market: string, quote: LotteryQuoteDto) => (
    <span className={'lottery-cell' + (quote.trend === 'up' ? ' up' : quote.trend === 'down' ? ' down' : '')} key={market + quote.selection}>
      <i>{lotterySelectionLabel(market, quote.selection)}</i>
      <b>{quote.odds.toFixed(2)}</b>
      <em>{trendArrow(quote.trend)}</em>
    </span>
  );
  return <div className={'lottery-block' + (compact ? ' compact' : '')}>
    <div className="lottery-line"><span className="lottery-block-title">竞彩 胜平负</span><div className="lottery-cells">{had.slice(0, 3).map((quote) => cell('had', quote))}</div></div>
    {hhad.length > 0 && <div className="lottery-line"><span className="lottery-block-title">让球 {hhad[0]?.lineText ?? ''}</span><div className="lottery-cells">{hhad.slice(0, 3).map((quote) => cell('hhad', quote))}</div></div>}
    {note && <div className="lottery-note">{note}</div>}
  </div>;
}


export function LotteryTrendSeries({ match, market, selection, label }: { match: MatchDetailDto; market: string; selection: string; label: string }) {
  const quotes = lotteryQuotes(match, market).filter((quote) => quote.selection === selection);
  if (quotes.length < 2) return null;
  const points: MovementStepDto[] = quotes.map((quote, index) => ({
    provider: 'sporttery',
    providerName: '竞彩',
    market,
    side: selection,
    sequence: index,
    lineText: quote.lineText,
    lineValue: quote.lineValue,
    odds: quote.odds,
    observedAt: quote.observedAt,
  }));
  return <MovementChart points={points} label={label + ' SP 变化'} />;
}

export function LotterySecondaryMarkets({ match }: { match: MatchDetailDto }) {
  const [open, setOpen] = useState(false);
  const markets = ['ttg', 'crs', 'hafu'] as const;
  const available = markets.filter((market) => lotteryQuotes(match, market).length > 0);
  if (available.length === 0) return null;
  const renderMarket = (market: string) => {
    const quotes = lotteryQuotes(match, market).filter((quote) => quote.selection !== 'goalLine');
    if (quotes.length === 0) return null;
    return <div className="lottery-market-group" key={market}>
      <span className="lottery-market-title">{LOTTERY_MARKET_LABELS[market]}</span>
      <div className="lottery-market-grid">{quotes.map((quote) => <span key={quote.selection} className={'lottery-market-cell' + (quote.trend === 'up' ? ' up' : quote.trend === 'down' ? ' down' : '')}><i>{lotterySelectionLabel(market, quote.selection)}</i><b>{quote.odds.toFixed(2)}</b><em>{trendArrow(quote.trend)}</em></span>)}</div>
    </div>;
  };
  return <section className="lottery-secondary">
    <button type="button" className="lottery-expand" onClick={() => setOpen((value) => !value)}>{open ? '收起全部玩法' : '展开全部玩法（总进球 / 比分 / 半全场）'}</button>
    {open && <div className="lottery-secondary-body">{markets.map(renderMarket)}</div>}
  </section>;
}

export function LotteryResultsPanel({ match }: { match: MatchDetailDto }) {
  const rows = match.lottery?.result ?? [];
  if (rows.length === 0) return null;
  return <section className="lottery-results">
    <div className="movement-heading"><h3>竞彩开奖结果</h3><span>{rows.length} 项</span></div>
    <div className="lottery-result-grid">{rows.map((row) => <div key={row.pool + ':' + row.combination}>
      <span>{LOTTERY_MARKET_LABELS[row.pool.toLowerCase()] ?? row.pool}</span>
      <b>{row.combinationDesc ?? row.combination}{row.goalLine ? ' (' + row.goalLine + ')' : ''}</b>
      <em>{row.sp ? row.sp.toFixed(2) : '—'}</em>
    </div>)}</div>
  </section>;
}

export function LotteryProbabilityPanel({ match }: { match: MatchDetailDto }) {
  const had = (side: string) => lotteryQuote(match, 'had', side)?.odds ?? null;
  const sportteryRates = impliedRates([had('h'), had('d'), had('a')]);
  const bookmakerOdds = (provider: Bookmaker) => ['home', 'draw', 'away'].map((side) => {
    const quote = quoteFor(match, provider, '1x2', side);
    return quote?.liveOdds ?? quote?.initialOdds ?? null;
  });
  const williamRates = impliedRates(bookmakerOdds('william'));
  const ladbrokesRates = impliedRates(bookmakerOdds('ladbrokes'));
  if (!sportteryRates && !williamRates && !ladbrokesRates) return null;
  const row = (label: string, rates: { probabilities: number[]; returnRate: number } | null, className: string) => rates && <div className={'probability-row ' + className}>
    <span>{label}</span>
    {rates.probabilities.map((value, index) => <b key={index}>{Math.round(value * 1000) / 10}%</b>)}
    <em>{Math.round(rates.returnRate * 1000) / 10}%</em>
  </div>;
  return <section className="lottery-probability">
    <div className="movement-heading"><h3>隐含概率 / 返还率</h3><span>三赔归一化</span></div>
    <div className="probability-table">
      <div className="probability-row head"><span>来源</span><b>主胜</b><b>平局</b><b>客胜</b><em>返还率</em></div>
      {row('竞彩 SP', sportteryRates, 'sporttery')}
      {row('威廉希尔', williamRates, 'william')}
      {row('立博', ladbrokesRates, 'ladbrokes')}
    </div>
  </section>;
}

export function LotteryPanel({ match, calculatorSelection, onCalculatorSelectionChange }: { match: MatchDetailDto; calculatorSelection?: CalculatorMatchSelection | null; onCalculatorSelectionChange?: (selection: CalculatorMatchSelection) => void }) {
  const had = lotteryQuotes(match, 'had').slice(0, 3);
  const hhad = lotteryQuotes(match, 'hhad').slice(0, 3);
  const note = dataNoteLabel(match.lottery?.dataNote);
  const cell = (market: string, quote: LotteryQuoteDto) => <div className={'lottery-detail-cell' + (quote.trend === 'up' ? ' up' : quote.trend === 'down' ? ' down' : '')} key={market + quote.selection}>
    <span>{lotterySelectionLabel(market, quote.selection)}</span>
    <b>{quote.odds.toFixed(2)}</b>
    <em>{trendArrow(quote.trend)}</em>
  </div>;
  return <section className="lottery-panel">
    <div className="lottery-head"><div><span className="ai-kicker">足彩计算器</span><h3>竞彩赔率{match.lottery?.number ? ' · ' + match.lottery.number : ''}</h3></div><span className="lottery-status">{note ?? '已同步'}</span></div>
    <div className="lottery-kickoff">开赛 {formatKickoff(match.kickoffTime)}{match.lottery?.businessDate ? ' · 竞彩批次 ' + match.lottery.businessDate : ''}</div>
    {had.length === 0 && hhad.length === 0 && <div className="state-box">{note ?? '暂无竞彩赔率数据'}</div>}
    {had.length > 0 && <div className="lottery-detail-block"><span className="lottery-block-title">胜平负</span><div className="lottery-detail-cells">{had.map((quote) => cell('had', quote))}</div></div>}
    {hhad.length > 0 && <div className="lottery-detail-block"><span className="lottery-block-title">让球胜平负 {hhad[0]?.lineText ?? ''}</span><div className="lottery-detail-cells">{hhad.map((quote) => cell('hhad', quote))}</div></div>}
    <div className="movement-grid">{(['h', 'd', 'a'] as const).map((side) => <LotteryTrendSeries key={'had' + side} match={match} market="had" selection={side} label={HAD_SIDE_LABELS[side] ?? side} />)}{(['h', 'd', 'a'] as const).map((side) => <LotteryTrendSeries key={'hhad' + side} match={match} market="hhad" selection={side} label={'让球 ' + HAD_SIDE_LABELS[side]} />)}</div>
    {onCalculatorSelectionChange && <LotteryCalculatorMarkets match={match} selection={calculatorSelection} onChange={onCalculatorSelectionChange} />}
    <LotteryResultsPanel match={match} />
    <LotteryProbabilityPanel match={match} />
  </section>;
}

